import { FC, useEffect, useMemo, useState } from 'react';
import { Avatar, Col, Flex, Row, Skeleton } from 'antd';
import {
    FaFacebook,
    FaGithub,
    FaInstagram,
    FaLinkedin,
    FaPinterest,
    FaReddit,
    FaSnapchat,
    FaTelegram,
    FaTumblr,
    FaWeixin,
    FaWhatsapp,
    FaYoutube,
} from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { IconType } from 'react-icons/lib';
import { SiCrunchbase } from 'react-icons/si';
import { TbBuilding, TbLink } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { resolveCorporateLogo } from '@/utilities';

import {
    FinancialOverviewEntityBannerProps,
    FinancialOverviewEntityLinksProps,
    FinancialOverviewSocialPlatform,
} from '../../types';

const socialLinkConfiguration: Array<{
    icon: IconType;
    key: FinancialOverviewSocialPlatform;
    label: string;
}> = [
    { icon: FaLinkedin, key: 'linkedin', label: 'LinkedIn' },
    { icon: FaSquareXTwitter, key: 'twitter', label: 'X' },
    { icon: FaFacebook, key: 'facebook', label: 'Facebook' },
    { icon: FaInstagram, key: 'instagram', label: 'Instagram' },
    { icon: FaYoutube, key: 'youtube', label: 'YouTube' },
    { icon: FaGithub, key: 'github', label: 'GitHub' },
    { icon: FaReddit, key: 'reddit', label: 'Reddit' },
    { icon: FaPinterest, key: 'pinterest', label: 'Pinterest' },
    { icon: FaSnapchat, key: 'snapchat', label: 'Snapchat' },
    { icon: FaTelegram, key: 'telegram', label: 'Telegram' },
    { icon: FaTumblr, key: 'tumblr', label: 'Tumblr' },
    { icon: FaWeixin, key: 'wechat', label: 'WeChat' },
    { icon: FaWhatsapp, key: 'whatsapp', label: 'WhatsApp' },
];

const avatarBannerOverlapPercentage = 60;
const bannerBottomSpacing = '1rem';

export const FinancialOverviewEntityLinks: FC<FinancialOverviewEntityLinksProps> = ({
    clearbit,
    loading,
    socials,
    website,
}) => {
    const { token } = useAntDesignTheme();
    const clearbitSocials = {
        facebook: clearbit?.facebook?.handle ? `https://facebook.com/${clearbit.facebook.handle}` : undefined,
        linkedin: clearbit?.linkedin?.handle ? `https://linkedin.com/${clearbit.linkedin.handle}` : undefined,
        twitter: clearbit?.twitter?.handle ? `https://x.com/${clearbit.twitter.handle}` : undefined,
    };
    const socialLinks = socialLinkConfiguration
        .map((configuration) => ({
            ...configuration,
            href: socials?.[configuration.key] ?? clearbitSocials[configuration.key as keyof typeof clearbitSocials],
        }))
        .filter((link): link is typeof link & { href: string } => Boolean(link.href));
    const crunchbaseHref = clearbit?.crunchbase?.handle
        ? `https://crunchbase.com/${clearbit.crunchbase.handle}`
        : undefined;

    if (!website && !socialLinks.length && !crunchbaseHref && !loading) {
        return null;
    }

    const linkStyle = { alignItems: 'center', color: token.colorTextTertiary, display: 'inline-flex' } as const;

    return (
        <Flex align='center' gap='0.75rem' wrap='wrap'>
            {website ? (
                <a
                    aria-label='Company website'
                    href={website}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={linkStyle}
                >
                    <TbLink size='0.95rem' />
                </a>
            ) : null}
            {loading
                ? Array.from({ length: 5 }, (_, index) => (
                      <span
                          aria-hidden='true'
                          data-testid='financial-overview-social-link-skeleton'
                          key={`social-link-skeleton-${index}`}
                          style={{ display: 'inline-flex' }}
                      >
                          <Skeleton.Avatar
                              active
                              shape='circle'
                              size='small'
                              style={{ height: '0.95rem', width: '0.95rem' }}
                          />
                      </span>
                  ))
                : socialLinks.map(({ href, icon: SocialIcon, key, label }) => (
                      <a
                          aria-label={label}
                          href={href}
                          key={key}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={linkStyle}
                      >
                          <SocialIcon size='0.95rem' />
                      </a>
                  ))}
            {!loading && crunchbaseHref ? (
                <a
                    aria-label='Crunchbase'
                    href={crunchbaseHref}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={linkStyle}
                >
                    <SiCrunchbase size='0.875rem' style={{ borderRadius: token.borderRadius }} />
                </a>
            ) : null}
        </Flex>
    );
};

export const FinancialOverviewEntityBanner: FC<FinancialOverviewEntityBannerProps> = ({
    actions,
    fallbackLogoUrl,
    logoUrl,
    name,
    ticker,
    website,
}) => {
    const { resolvedThemeName, token } = useAntDesignTheme();
    const logoTheme = String(resolvedThemeName).includes('dark') ? 'dark' : 'light';
    const logoCandidates = useMemo(
        () =>
            [
                resolveCorporateLogo(website, logoUrl, { theme: logoTheme }, { name, ticker }),
                resolveCorporateLogo(undefined, fallbackLogoUrl, { theme: logoTheme }, { name, ticker }),
            ].filter(
                (candidate, index, candidates): candidate is string =>
                    Boolean(candidate) && candidates.indexOf(candidate) === index,
            ),
        [fallbackLogoUrl, logoTheme, logoUrl, name, ticker, website],
    );
    const [failedLogoUrls, setFailedLogoUrls] = useState<ReadonlySet<string>>(() => new Set());
    const [displayedLogoUrl, setDisplayedLogoUrl] = useState<string | undefined>(() => logoCandidates[0]);
    const preferredLogoUrl = logoCandidates.find((candidate) => !failedLogoUrls.has(candidate));

    useEffect(() => {
        if (!preferredLogoUrl || preferredLogoUrl === displayedLogoUrl) {
            return;
        }

        if (typeof Image === 'undefined') {
            return;
        }

        // Brand enrichment can arrive after the profile image has already rendered.
        // Validate the new source off-screen so the visible avatar never flashes blank
        // while React hands off between asynchronous providers.
        const preloadedLogo = new Image();
        preloadedLogo.onload = () => setDisplayedLogoUrl(preferredLogoUrl);
        preloadedLogo.onerror = () => setFailedLogoUrls((failedUrls) => new Set(failedUrls).add(preferredLogoUrl));
        preloadedLogo.src = preferredLogoUrl;

        return () => {
            preloadedLogo.onload = null;
            preloadedLogo.onerror = null;
        };
    }, [displayedLogoUrl, preferredLogoUrl]);

    const handleLogoError = (): boolean => {
        if (!displayedLogoUrl) {
            return true;
        }

        const nextFailedLogoUrls = new Set(failedLogoUrls).add(displayedLogoUrl);
        setFailedLogoUrls(nextFailedLogoUrls);
        setDisplayedLogoUrl(logoCandidates.find((candidate) => !nextFailedLogoUrls.has(candidate)));

        // Keep Ant Design from replacing the image with the fallback glyph while
        // React advances to the next enriched source candidate.
        return false;
    };

    return (
        <Row align='top' gutter={[16, 16]} style={{ minHeight: '4rem', padding: '0 2rem', position: 'relative' }}>
            <Col flex='auto'>
                <div
                    style={{
                        left: '2rem',
                        position: 'absolute',
                        top: 0,
                        transform: `translateY(-${avatarBannerOverlapPercentage}%)`,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG + 8,
                            padding: '0.5rem',
                            position: 'relative',
                        }}
                    >
                        <div
                            aria-hidden='true'
                            data-testid='financial-overview-avatar-contour'
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadiusLG + 8,
                                boxSizing: 'border-box',
                                clipPath: `inset(0 0 calc(${100 - avatarBannerOverlapPercentage}% + ${bannerBottomSpacing}) 0)`,
                                inset: 0,
                                pointerEvents: 'none',
                                position: 'absolute',
                            }}
                        />
                        <div
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadiusLG,
                                overflow: 'hidden',
                            }}
                        >
                            <Avatar
                                shape='square'
                                style={{
                                    alignItems: 'center',
                                    backgroundColor: token.colorBgContainer,
                                    borderRadius: token.borderRadiusLG,
                                    color: token.colorTextTertiary,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                                size={112}
                                src={displayedLogoUrl}
                                onError={handleLogoError}
                                icon={
                                    <TbBuilding
                                        aria-hidden='true'
                                        data-testid='financial-overview-avatar-fallback-icon'
                                        size='3.5rem'
                                        style={{ color: token.colorTextTertiary, display: 'block' }}
                                    />
                                }
                            />
                        </div>
                    </div>
                </div>
            </Col>
            <Col flex='none' style={{ maxWidth: '60%', textAlign: 'right' }}>
                {actions ? <div style={{ marginTop: '0.125rem' }}>{actions}</div> : null}
            </Col>
        </Row>
    );
};
